// YNX — DIRECTORY MODULE (annuaire interne + extensions + messagerie)
import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, X, Phone, Mail, Send, Inbox } from 'lucide-react';
import {
  listContacts, createContact, deleteContact,
  listMessages, createMessage, markMessageRead, deleteMessage,
} from '../../lib/biz';

export const DirectoryModule = () => {
  const [tab, setTab] = useState('directory'); // 'directory' | 'messages'
  const [contacts, setContacts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showMsgForm, setShowMsgForm] = useState(null); // contact target
  const [form, setForm] = useState({ name: '', role: '', email: '', phone: '', department: '' });
  const [msgForm, setMsgForm] = useState({ from_extension: '', subject: '', body: '' });
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [cts, msgs] = await Promise.all([listContacts(), listMessages()]);
      setContacts(cts);
      setMessages(msgs);
    } catch (e) { console.warn('YNX directory: refresh failed', e); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const addContact = async () => {
    if (!form.name.trim()) return;
    setBusy(true);
    try {
      await createContact(form);
      setForm({ name: '', role: '', email: '', phone: '', department: '' });
      setShowForm(false);
      await refresh();
    } finally { setBusy(false); }
  };

  const removeContact = async (id) => {
    if (!window.confirm('Supprimer ce contact ?')) return;
    await deleteContact(id);
    await refresh();
  };

  const sendMsg = async () => {
    if (!msgForm.body.trim() || !msgForm.from_extension.trim()) return;
    setBusy(true);
    try {
      await createMessage({
        from_extension: msgForm.from_extension,
        to_extension: showMsgForm.extension,
        subject: msgForm.subject || `(de ${msgForm.from_extension})`,
        body: msgForm.body,
      });
      setMsgForm({ from_extension: msgForm.from_extension, subject: '', body: '' });
      setShowMsgForm(null);
      await refresh();
      alert('Message envoyé !');
    } finally { setBusy(false); }
  };

  const removeMsg = async (id) => {
    if (!window.confirm('Supprimer ce message ?')) return;
    await deleteMessage(id);
    await refresh();
  };

  return (
    <div className='biz-module'>
      <header className='biz-module-head'>
        <h2>📞 Annuaire & Téléphonie interne</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <div className='biz-segmented'>
            <button className={tab === 'directory' ? 'active' : ''} onClick={() => setTab('directory')}>Annuaire</button>
            <button className={tab === 'messages' ? 'active' : ''} onClick={() => setTab('messages')}>
              <Inbox size={12} /> Messages ({messages.filter((m) => !m.read).length})
            </button>
          </div>
          {tab === 'directory' && (
            <button className='biz-btn-primary sm' onClick={() => setShowForm(true)} data-testid='dir-add-btn'>
              <Plus size={14} /> Ajouter un contact
            </button>
          )}
        </div>
      </header>

      {tab === 'directory' && (
        <>
          <div className='biz-info-banner'>
            ℹ Chaque contact reçoit une <b>extension interne unique</b> (101, 102, 103…). Cliquez sur un téléphone pour lancer un appel direct depuis votre mobile (gratuit, pas de Twilio requis). Cliquez sur l'enveloppe pour envoyer un message interne.
          </div>
          {contacts.length === 0 && <div className='biz-empty'>Aucun contact. Ajoutez vos collaborateurs pour démarrer.</div>}
          <table className='biz-table'>
            <thead><tr><th>Ext.</th><th>Nom</th><th>Rôle</th><th>Département</th><th>Email</th><th>Téléphone</th><th></th></tr></thead>
            <tbody>
              {contacts.map((c) => (
                <tr key={c.id}>
                  <td><span className='biz-ext-pill'>{c.extension}</span></td>
                  <td><b>{c.name}</b></td>
                  <td>{c.role || '—'}</td>
                  <td>{c.department || '—'}</td>
                  <td>{c.email ? <a href={`mailto:${c.email}`}><Mail size={11} /> {c.email}</a> : '—'}</td>
                  <td>{c.phone ? <a href={`tel:${c.phone}`}><Phone size={11} /> {c.phone}</a> : '—'}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button className='biz-icon-btn' title='Message interne' onClick={() => { setShowMsgForm(c); setMsgForm({ from_extension: msgForm.from_extension || '', subject: '', body: '' }); }}>
                      <Send size={12} />
                    </button>
                    <button className='biz-icon-btn danger' onClick={() => removeContact(c.id)}>
                      <Trash2 size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {tab === 'messages' && (
        <>
          {messages.length === 0 && <div className='biz-empty'>Aucun message interne.</div>}
          <div className='biz-msg-list'>
            {messages.map((m) => (
              <article key={m.id} className={`biz-msg-item ${m.read ? '' : 'unread'}`}>
                <div className='biz-msg-meta'>
                  <span>De <b>ext. {m.from_extension}</b> → <b>ext. {m.to_extension}</b></span>
                  <span style={{ opacity: 0.6 }}>{new Date(m.created_at).toLocaleString('fr-FR')}</span>
                </div>
                <h4>{m.subject}</h4>
                <p>{m.body}</p>
                <div className='biz-msg-actions'>
                  {!m.read && <button className='biz-btn-ghost sm' onClick={async () => { await markMessageRead(m.id); refresh(); }}>Marquer lu</button>}
                  <button className='biz-btn-ghost sm danger' onClick={() => removeMsg(m.id)}>Supprimer</button>
                </div>
              </article>
            ))}
          </div>
        </>
      )}

      {showForm && (
        <div className='biz-modal-overlay' onClick={(e) => e.target === e.currentTarget && setShowForm(false)}>
          <div className='biz-modal'>
            <header className='biz-modal-head'><h3>Nouveau contact interne</h3>
              <button className='biz-icon-btn' onClick={() => setShowForm(false)}><X size={14} /></button>
            </header>
            <div className='biz-modal-body'>
              <label className='biz-label'>Nom complet *</label>
              <input className='biz-input' name='ctc-name' value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder='Jean Dupont' />
              <label className='biz-label'>Rôle / Poste</label>
              <input className='biz-input' name='ctc-role' value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder='Commercial, comptable, gérant…' />
              <label className='biz-label'>Département</label>
              <input className='biz-input' name='ctc-dept' value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} placeholder='Direction, RH, Vente…' />
              <label className='biz-label'>Email</label>
              <input className='biz-input' name='ctc-email' type='email' value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <label className='biz-label'>Téléphone externe</label>
              <input className='biz-input' name='ctc-phone' value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder='+33 6 12 34 56 78' />
              <p style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 8 }}>
                ▸ Une <b>extension interne unique</b> sera assignée automatiquement.
              </p>
            </div>
            <footer className='biz-modal-foot'>
              <button className='biz-btn-ghost' onClick={() => setShowForm(false)}>Annuler</button>
              <button className='biz-btn-primary' onClick={addContact} disabled={busy} data-testid='ctc-save-btn'>
                {busy ? 'Enregistrement…' : 'Créer'}
              </button>
            </footer>
          </div>
        </div>
      )}

      {showMsgForm && (
        <div className='biz-modal-overlay' onClick={(e) => e.target === e.currentTarget && setShowMsgForm(null)}>
          <div className='biz-modal'>
            <header className='biz-modal-head'>
              <h3>Message interne → ext. {showMsgForm.extension} ({showMsgForm.name})</h3>
              <button className='biz-icon-btn' onClick={() => setShowMsgForm(null)}><X size={14} /></button>
            </header>
            <div className='biz-modal-body'>
              <label className='biz-label'>Votre extension</label>
              <input className='biz-input' name='msg-from' value={msgForm.from_extension} onChange={(e) => setMsgForm({ ...msgForm, from_extension: e.target.value })} placeholder='100' />
              <label className='biz-label'>Sujet</label>
              <input className='biz-input' name='msg-subject' value={msgForm.subject} onChange={(e) => setMsgForm({ ...msgForm, subject: e.target.value })} placeholder='Rappel réunion 15h' />
              <label className='biz-label'>Message *</label>
              <textarea className='biz-input' name='msg-body' rows={5} value={msgForm.body} onChange={(e) => setMsgForm({ ...msgForm, body: e.target.value })} />
            </div>
            <footer className='biz-modal-foot'>
              <button className='biz-btn-ghost' onClick={() => setShowMsgForm(null)}>Annuler</button>
              <button className='biz-btn-primary' onClick={sendMsg} disabled={busy} data-testid='msg-send-btn'>
                {busy ? 'Envoi…' : 'Envoyer'}
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
};
